import { classify } from '../../config/elementCoercion';
import { normalizeId } from '../../utils/ids';

/**
 * The multi add editor's pure model: what the mapping picker may offer for
 * one element of one step, and why an option is greyed. Mirrors the API's
 * write-time rules (keepr-api utils/multiAddForms/validate.js) so the
 * editor never offers a mapping the save would refuse:
 *
 *   input     the coercion matrix over (input type → element type), plus
 *             the lookup lineage and cardinality rules
 *   element   an element of an EARLIER, non-repeating step, not omitted
 *             there, same matrix and lookup rules
 *   step      a card-lookup element only; an earlier step whose card is the
 *             lookup's card or a descendant; from a repeated source only
 *             into an allowMultiple lookup; never repeated → repeated
 *
 * Everything here is data in, data out — the page renders it.
 */

export const MAPPING = { ASK: 'ask', OMIT: 'omit', STATIC: 'static', INPUT: 'input', ELEMENT: 'element', STEP: 'step' };

// "The server owns this value", whichever of the reasons applies:
// `drivenFrom` (recomputed from related records), `sequence` (a number
// allocated at create) or a "Me too" count (KPR-390, `confirmations`: keepr
// counts it from the people who confirm). Every use here is asking whether a
// person or a mapping may put a value there — or copy one from it — and for
// that question they are one answer: the API refuses a mapping to any of
// them (400 naming the field), and from a count ("a new item has none to
// copy"). So a step never lists one, as a field or as a source.
export const isDriven = element => !!(element?.options?.drivenFrom || element?.options?.sequence || element?.dataType === 'confirmations');
export const labelOf = element => element?.label?.singular || element?.name || '';

/** A URL-safe slug the API accepts as a key (lowercase letters, digits, hyphens; ≤ 50). */
export function slugify(text, fallback = 'step') {
	const slug = String(text || '')
		.toLowerCase()
		.normalize('NFKD')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, 50)
		.replace(/-+$/g, '');
	return slug || fallback;
}

/** `base`, or `base-2`, `base-3`… until it is not in `taken`. */
export function uniqueKey(base, taken = new Set()) {
	if (!taken.has(base)) return base;
	for (let n = 2; n < 1000; n++) {
		const candidate = `${base.slice(0, 50 - String(n).length - 1)}-${n}`;
		if (!taken.has(candidate)) return candidate;
	}
	return `${base}-${Date.now()}`;
}

/** An ad hoc input as the element definition the matrix and the renderer read. */
export const inputAsElement = input => ({
	name: input.key,
	label: { singular: input.label || input.key, plural: input.label || input.key },
	dataType: input.dataType,
	options: input.options || {},
});

/** Is `cardId` equal to `rootId` or a descendant of it (walking the cache)? No root constrains nothing. */
export function cardDescendsFrom(cardsById, cardId, rootId) {
	const root = normalizeId(rootId);
	if (!root) return true;
	let current = normalizeId(cardId);
	const seen = new Set();
	while (current && !seen.has(current)) {
		if (current === root) return true;
		seen.add(current);
		current = normalizeId(cardsById?.[current]?.parentCardId);
	}
	return false;
}

/**
 * The pair rules for a source element feeding a target element:
 * -> { ok: true, transforms } | { ok: false, reason }
 * `sourceCardId` stands in for a step reference (the created item's card).
 */
export function pairLegality({ source, target, cardsById, sourceCardId = null }) {
	if (target.dataType === 'card-lookup') {
		const sourceCard = sourceCardId || normalizeId(source?.options?.lookupCardId);
		if (!sourceCardId && source.dataType !== 'card-lookup') {
			return { ok: false, reason: 'Card lookups can only be filled by card lookups or by an item this form creates.' };
		}
		if (!sourceCard) return { ok: false, reason: 'The source lookup does not name a card.' };
		if (!cardDescendsFrom(cardsById, sourceCard, target?.options?.lookupCardId)) {
			return { ok: false, reason: 'The source refers to a card this lookup does not accept.' };
		}
		const sourceMulti = !sourceCardId && !!source?.options?.allowMultiple;
		const targetMulti = !!target?.options?.allowMultiple;
		if (sourceMulti && !targetMulti) return { ok: false, reason: 'A multi-value lookup cannot fill a single-value one.' };
		if (sourceCardId) return { ok: true, transforms: ['none'] };
	}
	const cls = classify(source.dataType, target.dataType, { sourceOptions: source?.options || {}, targetOptions: target?.options || {} });
	if (cls.class === 'unmappable') return { ok: false, reason: cls.reason };
	// choice → choice is "exact" to the matrix, but the API checks the VALUE
	// against the target's set at submit — on a field the page never shows.
	// Only offer it when every source value is one of the target's.
	if (source.dataType === 'choice' && target.dataType === 'choice') {
		const values = choices => (Array.isArray(choices) ? choices : []).map(c => (c && typeof c === 'object' ? String(c.value) : String(c)));
		const targetValues = values(target?.options?.choices);
		const sourceValues = values(source?.options?.choices);
		if (targetValues.length && sourceValues.some(v => !targetValues.includes(v))) {
			return { ok: false, reason: 'Its choices are not all among this element\'s choices.' };
		}
	}
	return { ok: true, transforms: cls.transforms || ['none'] };
}

/**
 * Drops every mapping the picker would no longer offer — after a step's
 * card, repeat flag or order changed, an input's type or options changed,
 * anything. The same `mappingOptions` the picker renders is the judge, so
 * the editor can never save what it would not offer. A kept mapping keeps
 * its transform when still legal, else the first legal one.
 * -> { steps, changed }
 */
export function pruneIllegalMappings({ steps, inputs, cardsById, elementsByStep }) {
	let changed = false;
	const next = steps.map((step, stepIndex) => {
		const fields = {};
		Object.entries(step.fields || {}).forEach(([name, mapping]) => {
			if (!mapping) return;
			if (mapping.from === MAPPING.OMIT || mapping.from === MAPPING.STATIC) { fields[name] = mapping; return; }
			const element = (elementsByStep[step.key] || []).find(el => el.name === name);
			if (!element) { fields[name] = mapping; return; } // unknown card: nothing to judge against
			const option = mappingOptions({ element, stepIndex, steps, inputs, cardsById, elementsByStep }).find(o => o.value === mappingValueOf(mapping));
			if (!option || !option.ok) { changed = true; return; }
			const legal = option.transforms || ['none'];
			const type = mapping.transform?.type;
			if ((mapping.from === MAPPING.INPUT || mapping.from === MAPPING.ELEMENT) && !legal.includes(type)) {
				changed = true;
				fields[name] = { ...mapping, transform: { type: legal[0] } };
				return;
			}
			fields[name] = mapping;
		});
		return Object.keys(fields).length === Object.keys(step.fields || {}).length && !changed ? step : { ...step, fields };
	});
	return { steps: changed ? next : steps, changed };
}

/**
 * Every mapping the picker may offer for `element` on the step at
 * `stepIndex`, each `{ value, label, group, ok, reason, transforms,
 * sourceOptional }`. `value` is the serialized mapping: 'ask' | 'omit' |
 * 'static' | 'input:<key>' | 'element:<step>:<name>' | 'step:<step>'.
 * `sourceOptional` is true when the option reads an earlier step the
 * person adding may leave out — such a mapping resolves blank then, which
 * the API refuses on a required element; the page says so under the row.
 */
export function mappingOptions({ element, stepIndex, steps, inputs, cardsById, elementsByStep }) {
	const step = steps[stepIndex];
	const required = !!element?.options?.required;
	// A file element is never filled by a multi add form (PR 10b): the form
	// uploads nothing, so it can neither ask for one nor fix one — and no input
	// or earlier value converts into a file (config/elementCoercion.js).
	const fileReason = element?.dataType === 'file' ? 'A multi add form cannot collect a file.' : null;
	const out = [
		{ value: MAPPING.ASK, label: 'Ask on the form', group: 'basics', ok: !fileReason, reason: fileReason, sourceOptional: false },
		{ value: MAPPING.OMIT, label: 'Skip (leave blank)', group: 'basics', ok: !required, reason: required ? 'A required element cannot be skipped.' : null, sourceOptional: false },
		{ value: MAPPING.STATIC, label: 'Fixed value', group: 'basics', ok: !fileReason, reason: fileReason, sourceOptional: false },
	];
	inputs.forEach(input => {
		const legality = pairLegality({ source: inputAsElement(input), target: element, cardsById });
		out.push({ value: `${MAPPING.INPUT}:${input.key}`, label: input.label || input.key, group: 'input', sourceOptional: false, ...legality });
	});
	for (let i = 0; i < stepIndex; i++) {
		const earlier = steps[i];
		const earlierCard = cardsById?.[normalizeId(earlier.card_id)];
		const earlierLabel = earlier.label || earlierCard?.name || earlier.key;
		const sourceOptional = !earlier.required;
		if (!earlier.repeat) {
			(elementsByStep[earlier.key] || []).forEach(source => {
				if (isDriven(source)) return;
				const mapping = earlier.fields?.[source.name];
				if (mapping && mapping.from === MAPPING.OMIT) return;
				const legality = pairLegality({ source, target: element, cardsById });
				out.push({ value: `${MAPPING.ELEMENT}:${earlier.key}:${source.name}`, label: `${earlierLabel} › ${labelOf(source)}`, group: 'element', sourceOptional, ...legality });
			});
		}
		if (element?.dataType === 'card-lookup' && earlierCard) {
			let legality = pairLegality({ source: null, target: element, cardsById, sourceCardId: normalizeId(earlierCard._id) });
			if (legality.ok && earlier.repeat && step.repeat) legality = { ok: false, reason: 'A repeated step cannot reference another repeated step.' };
			else if (legality.ok && earlier.repeat && !element?.options?.allowMultiple) legality = { ok: false, reason: `${earlierLabel} repeats, so only a lookup that allows multiple values can hold all of its items.` };
			out.push({ value: `${MAPPING.STEP}:${earlier.key}`, label: `The ${earlierLabel} this form creates`, group: 'step', sourceOptional, ...legality });
		}
	}
	return out;
}

/** 'input:<key>' → { from, ... } (transform settled by the caller). */
export function parseMappingValue(value) {
	if (!value || value === MAPPING.ASK) return null;
	if (value === MAPPING.OMIT) return { from: MAPPING.OMIT };
	if (value === MAPPING.STATIC) return { from: MAPPING.STATIC, value: undefined };
	const [from, ...rest] = value.split(':');
	if (from === MAPPING.INPUT) return { from, input: rest.join(':') };
	if (from === MAPPING.ELEMENT) return { from, step: rest[0], element: rest.slice(1).join(':') };
	if (from === MAPPING.STEP) return { from, step: rest.join(':') };
	return null;
}

/** The inverse: a stored mapping → the picker value. */
export function mappingValueOf(mapping) {
	if (!mapping) return MAPPING.ASK;
	if (mapping.from === MAPPING.INPUT) return `${MAPPING.INPUT}:${mapping.input}`;
	if (mapping.from === MAPPING.ELEMENT) return `${MAPPING.ELEMENT}:${mapping.step}:${mapping.element}`;
	if (mapping.from === MAPPING.STEP) return `${MAPPING.STEP}:${mapping.step}`;
	return mapping.from;
}

/**
 * "Ask once": for every asked element of the step at `stepIndex`, the
 * nearest earlier non-repeating step with an element of the same name and
 * type (and, for lookups, the same card), not omitted there. Returns the
 * `{ [name]: mapping }` additions.
 */
export function askOnceSuggestions({ stepIndex, steps, elementsByStep, cardsById }) {
	const step = steps[stepIndex];
	const additions = {};
	(elementsByStep[step.key] || []).forEach(element => {
		if (isDriven(element) || step.fields?.[element.name]) return;
		for (let i = stepIndex - 1; i >= 0; i--) {
			const earlier = steps[i];
			if (earlier.repeat) continue;
			const source = (elementsByStep[earlier.key] || []).find(el => el.name === element.name && el.dataType === element.dataType && !isDriven(el));
			if (!source) continue;
			if (earlier.fields?.[source.name]?.from === MAPPING.OMIT) continue;
			if (element.dataType === 'card-lookup' && normalizeId(source.options?.lookupCardId) !== normalizeId(element.options?.lookupCardId)) continue;
			const legality = pairLegality({ source, target: element, cardsById });
			if (!legality.ok) continue;
			additions[element.name] = { from: MAPPING.ELEMENT, step: earlier.key, element: source.name, transform: { type: legality.transforms[0] || 'none' } };
			break;
		}
	});
	return additions;
}

/** Inputs no mapping receives — the API refuses the form while any exist. */
export function unmappedInputs(inputs, steps) {
	const used = new Set();
	steps.forEach(step => Object.values(step.fields || {}).forEach(mapping => {
		if (mapping?.from === MAPPING.INPUT && mapping.input) used.add(mapping.input);
	}));
	return inputs.filter(input => !used.has(input.key));
}

/** Rewrites every reference to `oldKey` (a step key) as `newKey`, dropping mappings that pointed at a removed step (`newKey` null). */
export function rekeyStepReferences(steps, oldKey, newKey) {
	return steps.map(step => {
		const fields = {};
		Object.entries(step.fields || {}).forEach(([name, mapping]) => {
			if ((mapping?.from === MAPPING.ELEMENT || mapping?.from === MAPPING.STEP) && mapping.step === oldKey) {
				if (newKey) fields[name] = { ...mapping, step: newKey };
				return;
			}
			fields[name] = mapping;
		});
		return { ...step, fields };
	});
}

/** Drops every mapping that reads the input `key`. */
export function dropInputReferences(steps, key) {
	return steps.map(step => {
		const fields = {};
		Object.entries(step.fields || {}).forEach(([name, mapping]) => {
			if (mapping?.from === MAPPING.INPUT && mapping.input === key) return;
			fields[name] = mapping;
		});
		return { ...step, fields };
	});
}

/** The wire body the API takes (create and update alike). */
export function buildFormBody({ name, linkItems, inputs, steps }) {
	return {
		name: String(name || '').trim(),
		linkItems: !!linkItems,
		inputs: inputs.map(input => ({ key: input.key, label: String(input.label || '').trim(), dataType: input.dataType, options: input.options || {} })),
		steps: steps.map(step => ({
			key: step.key,
			card_id: normalizeId(step.card_id),
			label: step.label ? String(step.label).trim() : null,
			// Off by default: a step the person adding may leave out. On, every
			// submission adds the card (the page locks its include toggle on).
			required: !!step.required,
			repeat: step.repeat ? { min: step.repeat.min ?? 1, max: step.repeat.max ?? 50, ...(step.repeat.label ? { label: step.repeat.label } : {}) } : null,
			fields: step.fields || {},
			// The tags the step's items start with (tags T9): the runner shows
			// them, and applies what the person keeps. Always sent — `[]` clears.
			tagIds: Array.isArray(step.tagIds) ? step.tagIds : [],
			// The lines a repeating step OPENS with, from the data. Only a
			// repeating step can carry one (the API refuses the pair), so a
			// step that stops repeating drops its seed with it rather than
			// saving config that can never run.
			...(step.repeat && step.seedFrom && step.seedFrom.card_id ? {
				seedFrom: {
					card_id: normalizeId(step.seedFrom.card_id),
					...(step.seedFrom.kql ? { kql: String(step.seedFrom.kql).trim() } : {}),
					...(Number.isFinite(Number(step.seedFrom.max)) ? { max: Number(step.seedFrom.max) } : {}),
					fields: step.seedFrom.fields || {},
				},
			} : {}),
		})),
	};
}
